// Cliente 360 (Ola 3) — unified client data card for the Info tab: account /
// billing fields, contact fields, audit data and ONE aggregated missing-info
// notice in a single DashboardCard (replaces the former split Cuenta +
// Contacto cards). Name / status / type / code live in the page header, so
// they are intentionally NOT repeated here.
//
// Optional fields with no value never render an empty '—' grid row: present
// fields are shown, absent ones are listed compactly at the foot
// (progressive disclosure, H8) through a single aggregated InfoFaltante.
import { Alert, Divider, Spin, Typography, theme } from 'antd';
import type { ReactNode } from 'react';
import { DashboardCard } from '../../../components/DashboardCard';
import { CampoInfo, CamposInfoGrid, InfoFaltante } from '../../../components/CampoInfo';
import { useT } from '../../../locales';
import { EmptyState } from '../../../components/EmptyState';
import { StatusTag, type StatusTagTone } from '../../../components/StatusTag';
import { useClienteEventos } from '../api/useClientes';
import type { ClienteEventoTipo, ClienteResponse } from '@aid/shared';
import { VerMapaButton } from '../../../components/VerMapaButton';
import { parseDateOnly } from '../../../lib/date';

const { Text } = Typography;
const DATE_FORMAT = 'DD/MM/YYYY';

interface ClienteInfoGeneralCardProps {
  cliente: ClienteResponse;
}

interface CampoPendiente {
  label: string;
  value: ReactNode;
}

function formatDate(value: Date | string | null): string | null {
  return parseDateOnly(value)?.format(DATE_FORMAT) ?? null;
}

/** StatusTag tone per lifecycle event type (client 360 Eventos section). */
const EVENTO_TONE: Record<ClienteEventoTipo, StatusTagTone> = {
  ALTA: 'success',
  BAJA: 'error',
  REACTIVACION: 'warning',
};

/** Translated label per lifecycle event type (client 360 Eventos section). */
const EVENTO_LABEL_KEY: Record<ClienteEventoTipo, 'tipoAlta' | 'tipoBaja' | 'tipoReactivacion'> = {
  ALTA: 'tipoAlta',
  BAJA: 'tipoBaja',
  REACTIVACION: 'tipoReactivacion',
};

export function ClienteInfoGeneralCard({ cliente }: ClienteInfoGeneralCardProps) {
  const t = useT();
  const { token } = theme.useToken();
  const eventosQuery = useClienteEventos(cliente.id);

  // Present-optional contract: every field below is nullable in the domain,
  // so each either fills a grid row or joins the aggregated missing list.
  const campos: CampoPendiente[] = [];
  const faltantes: string[] = [];

  const pushCampo = (label: string, value: ReactNode, presente: boolean) => {
    if (presente) {
      campos.push({ label, value });
    } else {
      faltantes.push(label);
    }
  };

  pushCampo(
    t('pages.clientes.phone'),
    cliente.telefonoPrincipal ?? undefined,
    Boolean(cliente.telefonoPrincipal),
  );
  pushCampo(
    t('pages.clientes.phoneReference'),
    cliente.telefonoReferencia ?? undefined,
    Boolean(cliente.telefonoReferencia),
  );
  pushCampo(t('pages.clientes.email'), cliente.email ?? undefined, Boolean(cliente.email));
  pushCampo(
    t('pages.clientes.identificacion'),
    cliente.identificacion ?? undefined,
    Boolean(cliente.identificacion),
  );
  pushCampo(
    t('pages.clientes.diaCobro'),
    cliente.diaCobro?.toString(),
    cliente.diaCobro !== null && cliente.diaCobro !== undefined,
  );
  pushCampo(
    t('pages.clientes.tipoSuscripcion'),
    cliente.tipoSuscripcion?.value ?? cliente.tipoSuscripcion?.key,
    Boolean(cliente.tipoSuscripcion),
  );
  const fechaCobroFormateada = formatDate(cliente.fechaCobro);
  pushCampo(
    t('pages.clientes.fechaCobro'),
    fechaCobroFormateada ?? undefined,
    Boolean(fechaCobroFormateada),
  );
  pushCampo(t('pages.clientes.nit'), cliente.nit ?? undefined, Boolean(cliente.nit));
  pushCampo(
    t('pages.clientes.direccion'),
    cliente.direccion ?? undefined,
    Boolean(cliente.direccion),
  );
  pushCampo(
    t('pages.clientes.ubicacionGps'),
    cliente.ubicacion ? (
      <VerMapaButton latitud={cliente.ubicacion.latitud} longitud={cliente.ubicacion.longitud} />
    ) : undefined,
    Boolean(cliente.ubicacion),
  );
  const nacimientoFormateada = formatDate(cliente.fechaNacimiento);
  pushCampo(
    t('pages.clientes.fechaNacimiento'),
    nacimientoFormateada ?? undefined,
    Boolean(nacimientoFormateada),
  );

  // Auditoría: only fields with a value render (the absence of "modificado
  // por" or comments is valid information, not missing data).
  const auditoria = [
    { label: t('pages.clientes.createdAt'), value: formatDate(cliente.fechaCreacion) },
    { label: t('common.updatedAt'), value: formatDate(cliente.fechaModificacion) },
    { label: t('pages.clientes.modificadoPor'), value: cliente.usuarioModifica ?? null },
    { label: t('pages.clientes.comments'), value: cliente.comentarios ?? null, full: true },
  ].filter((campo) => campo.value !== null);

  // Eventos: per-client lifecycle events (public.cliente_evento), newest
  // first. Fetched directly from cliente.id, same pattern as the sibling
  // Info-tab cards (ClienteKPIsFinancieros).
  const eventos = eventosQuery.data ?? [];

  return (
    <DashboardCard title={t('pages.clientes.infoGeneral')} style={{ height: '100%' }}>
      <CamposInfoGrid>
        {campos.map((campo) => (
          <CampoInfo key={campo.label} label={campo.label} value={campo.value} />
        ))}
      </CamposInfoGrid>

      {auditoria.length > 0 && (
        <>
          <Divider
            style={{
              margin: '22px 0 18px',
              borderColor: token.colorSplit,
            }}
          />
          <Text
            type="secondary"
            style={{
              display: 'block',
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.4,
              marginBottom: 14,
            }}
          >
            {t('pages.clientes.infoAuditoria')}
          </Text>
          <CamposInfoGrid>
            {auditoria.map((campo) => (
              <CampoInfo
                key={campo.label}
                label={campo.label}
                value={campo.value ?? undefined}
                full={campo.full}
              />
            ))}
          </CamposInfoGrid>
        </>
      )}

      {/* Eventos: lifecycle events, below the Auditoría block. */}
      <Divider
        style={{
          margin: '22px 0 18px',
          borderColor: token.colorSplit,
        }}
      />
      <Text
        type="secondary"
        style={{
          display: 'block',
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1.4,
          marginBottom: 14,
        }}
      >
        {t('pages.clientes.infoEventos')}
      </Text>
      {eventosQuery.isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '14px 0' }}>
          <Spin size="small" />
        </div>
      ) : eventosQuery.isError ? (
        <Alert
          type="error"
          showIcon
          message={t('pages.clientes.eventos.errorTitle')}
          description={t('pages.clientes.eventos.errorDesc')}
        />
      ) : eventos.length === 0 ? (
        <EmptyState compact title={t('pages.clientes.eventos.sinEventos')} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {eventos.map((evento) => {
            const fuente = evento.metadata?.fuente;
            const fuenteLabel =
              typeof fuente === 'string' && fuente.length > 0 ? ` (${fuente})` : '';
            // One compact line: tipo tag, then date · actor (actor hidden
            // for 'system' rows) and the metadata.fuente sidecar when set.
            const detalles = [
              formatDate(evento.fechaEvento),
              evento.actor && evento.actor !== 'system' ? evento.actor : null,
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <div
                key={evento.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 12,
                  lineHeight: 1.4,
                }}
              >
                <StatusTag tone={EVENTO_TONE[evento.tipoEvento]} style={{ fontSize: 12 }}>
                  {t(`pages.clientes.eventos.${EVENTO_LABEL_KEY[evento.tipoEvento]}`)}
                </StatusTag>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {detalles}
                  {fuenteLabel}
                </Text>
              </div>
            );
          })}
        </div>
      )}

      {/* Missing-info footer: always last, separated from the sections above
          with the same Divider, and rendered only when something is actually
          missing (no dangling line on complete files). The footer Divider
          carries no bottom margin: InfoFaltante ships its own 16px
          marginTop, so the line-to-text rhythm stays equal to the other
          sections. */}
      {faltantes.length > 0 && (
        <>
          <Divider
            style={{
              margin: '22px 0 0',
              borderColor: token.colorSplit,
            }}
          />
          <InfoFaltante intro={t('pages.clientes.infoFaltante')} labels={faltantes} />
        </>
      )}
    </DashboardCard>
  );
}
